import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  REVIVE_BLOCKED_COPY,
  REVIVE_SHEET_COPY,
  formatKrw,
  imageStatusLabel,
  imageSubline,
  jobProviderRecallCount,
  recallBlockKind,
  recallBlockOf,
  isResultReceiveClosed,
  isResultReceiveExpired,
  isRemakeRejectedDiscarded,
  remakeOutcomeMessage,
  RESULT_RECEIVE_EXPIRED_CODE,
  remakeSheetTitle,
  retentionEndedBlockCopy,
  reviveBlockedTitle,
  reviveFailedText,
  reviveSheetBody,
  roundEndAmountLabel,
  type DetailJobView,
  type JobImageView,
  type RemakeErrorLike,
} from "@makit/shared";

import { PageHead } from "../../app/PageHead";
import { Button, StatusChip } from "../../components/primitives";
import { LimitBanner, PayNow } from "../../components/banners";
import { EmptyState } from "../../components/feedback";
import { ApiError, reviveImage } from "../../lib/api";
import { settlementPath } from "../../lib/settlement";
import {
  reviveBranchOf,
  uploadStageFoldLine,
  uploadStageRows,
  uploadStageShowsJob,
  type ShellJob,
} from "../../lib/shell-job";
import { useJobView } from "../../lib/use-job-view";

/** 어느 장을 가리키며 들어왔는가. 실리면 그 장의 패널을 먼저 연다(그 장이 다시 만들 수 있는 장일 때.
 *  아니면 다시 만들 수 있는 첫 장으로 떨어진다. 아래 `target`).
 *
 * **싣는 문은 지금 하나다.** `W-31` 의 `이 장 다시 만들기`(`Result.tsx`, PRD `7.13` D-95 ⓑ. 어느 장을 싣는지인 ⓐ 는 미결). ⚠️ `W-40c` 의
 * `그 장 다시 만들기 ›` 는 장을 알지만 아직 작업 주소만 싣는다(`Export.tsx`. 이 회차 자리 밖). */
const IMAGE_PARAM = "image";

/** 그 장을 가리키며 이 화면으로 오는 주소. **문마다 `?image=` 를 손으로 적지 않는다.** 낱말이 갈리면
 *  여는 쪽은 실었다고 믿고 이 화면은 못 읽어, 셀러가 고르지 않은 장이 열린다(D-95 가 막으려던 것). */
export function uploadStagePathFor(jobId: string, imageId: string): string {
  return `/jobs/${jobId}/upload?${IMAGE_PARAM}=${encodeURIComponent(imageId)}`;
}

/** `W-21` 올리는 중. 앱 `A-22`·`A-25` 가 여기 모인다.
 *
 * **폰은 두 화면, 웹은 한 화면이다.** 확인 시트가 목록을 가리는 폰과 달리 웹은 목록을
 * 읽으면서 금액을 확인할 수 있다. 오른쪽 패널은 "다른 기능"이 아니라 **다른 상태**다.
 *
 * ## 문 셋이 여기로 온다
 *
 * `W-31` 의 `이 장 다시 만들기` · `W-40c` 의 `그 장 다시 만들기 ›` · `W-30` 의 `올리는 중 화면`.
 * 셋 다 **그 작업의** 이 화면으로 오고 **그 작업의 장**을 그린다. `W-31` 은 **어느 장인지도 싣는다**
 * (`uploadStagePathFor`).
 *
 * ⛔ **예시 갈래(`SampleUploading`)를 걷었다** (2026-10-04 사용자 지시). 예시 작업이 없어졌고,
 * 사진 0장 · 사진 지워짐 작업에서 남의 파일 목록(`IMG_4021.jpg` …)을 그리던 자리였다. 그 둘은
 * 이제 빈 상태(`UploadEmpty`)가 받는다. ⚠️ `lib/shell-job.ts` 의 `uploadStageShowsJob` 머리말
 * 「예시를 그대로 둔다」는 이 회차 자리 밖이라 낡은 채 남았다. */
export function UploadingStage() {
  const job = useOutletContext<ShellJob>();
  const { view } = useJobView(job.id);

  // 셸이 같은 질의를 받아 온 뒤에만 이 화면이 선다(`JobShell`). 그래도 비면 그리지 않는다.
  if (!view) return null;
  if (!uploadStageShowsJob(view)) return <UploadEmpty discarded={view.status === "DISCARDED"} />;
  return <JobUploading view={view} />;
}

/** 사진 0장 · 사진 지워짐 작업. 두 상태가 설 화면이 목업에 없다(PRD `7.13` D-39).
 *
 * 문구는 새로 짓지 않고 있는 것을 쓴다(design-manager 보수적 권고, 2026-10-04).
 * - 사진 지워짐: 제목은 작업 상태 이름(`packages/shared/src/job-labels.ts`), 본문은
 *   `lib/edit-messages.ts` 의 `JOB_DISCARDED` 문장.
 * - 사진 0장: 앱 `detail/order.tsx` 의 빈 상태.
 * 버튼은 갈 수 있는 곳 하나(`내 작업`. `JobNotFound` 와 같은 낱말)만 둔다. */
function UploadEmpty({ discarded }: { discarded: boolean }) {
  const navigate = useNavigate();
  return (
    <>
      <PageHead frame="W-21" title="올리는 중" />
      <EmptyState
        art="▤"
        title={discarded ? "사진 지워짐" : "담긴 사진이 없어요"}
        body={
          discarded
            ? "이 작업의 원본 사진이 없어져 더 진행할 수 없습니다."
            : "이 작업에 올라간 사진이 없습니다."
        }
        action={<Button label="내 작업" onClick={() => navigate("/jobs")} />}
      />
    </>
  );
}

/** `RetrySheet`(앱)의 `asRemakeError` 와 같다. `request()` 는 실패를 `ApiError` 로 바꿔
 *  던지지만, 아닌 것이 오면 **`답이 없었다`** 로 떨어뜨린다. 돈에 대해 단정하지 않는 유일한
 *  갈래라 모르는 실패가 갈 자리가 거기다. */
function asRemakeError(error: unknown): RemakeErrorLike {
  return error instanceof ApiError ? { status: error.status, code: error.code } : { status: 0 };
}

/** 서버 작업의 `W-21`. **못 만든 장 다시 만들기만 실제로 한다.**
 *
 * ⛔ **만든 장 다시 만들기(앱 `A-25`, 장당 ₩500)는 열지 않는다.** 그 장에는 `↻` 가 서지
 * 않는다. 판정은 `reviveBranchOf` 한 자리다(그 함수 머리말의 M-14).
 *
 * **패널의 글자는 앱 `A-25b`·`A-25c` 와 같은 한 벌이다**(`packages/shared/src/remake-messages.ts`
 * 의 `REVIVE_SHEET_COPY` · `REVIVE_BLOCKED_COPY`, 목업 `A-25b`·`A-25c`). 갈래를 고르는 함수도
 * 같다. 다른 것은 하나뿐이다. 웹은 **`다시 찍는 편`이 아니라 `다시 올리는 편`** 이다. PC 에는
 * 카메라가 없다(목업 `W-21`). 그래서 본문은 `reviveSheetBody("WEB", …)` 로 부른다.
 * ⛔ 이 파일에 그 문장을 손으로 다시 적지 않는다. `scripts/web-revive.test.mjs` ④ 가 막는다. */
function JobUploading({ view }: { view: DetailJobView }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const rows = uploadStageRows(view.images);
  const foldLine = uploadStageFoldLine(rows, view.images.length);
  const done = view.images.filter((image) => image.status === "DONE").length;

  /** `undefined` 는 아직 아무것도 안 골랐다는 뜻이다. 그때는 들어온 문이 가리킨 장, 없으면
   *  다시 만들 수 있는 첫 장을 가리킨다. `null` 은 셀러가 `그만두기` 로 비운 것이다. */
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  /** 끝나면 결과로 데려갈 것인가. 도는 동안 `닫기` 를 누르면 끈다. ⚠️ 요청은 멈추지 않는다
   *  (`닫아도 계속 만듭니다`). 끝나면 캐시는 새 작업으로 바뀌고, 화면만 셀러를 끌고 가지 않는다. */
  const follow = useRef(false);
  // ⛔ **화면을 떠나면 끈다.** `useMutation` 의 `onSuccess` 는 이 화면이 사라진 뒤에도 돌고
  // `navigate` 도 그때 그대로 이동한다. 끄지 않으면 다른 화면으로 간 셀러를 이 작업으로
  // 끌고 온다.
  useEffect(
    () => () => {
      follow.current = false;
    },
    [],
  );
  /** 서버에 나가 있는 되살리기 요청의 장. **`revive.reset()` 과 상관없이 응답이 올 때까지
   *  남는다.** 도는 동안 `닫기` 를 누르면 패널은 닫히지만 요청은 이어지고(`닫아도 계속
   *  만듭니다`), 그 사이 `↻` 를 다시 세우면 같은 작업에 두 번째 요청이 나가 서버 잠금의 409
   *  가 `다른 장을 만들고 있어요` 로 읽힌다. 원가는 안 나가지만 말이 어긋난다.
   *  ⚠️ 앱 `A-31` 도 시트를 닫으면 같은 틈이 있다(`retry-sheet.tsx` 의 `close`). 웹은 한
   *  화면에 목록과 패널이 함께 있어 그 틈이 바로 눌리므로 여기서 막는다. 문구는 앱과 같다. */
  const [runningId, setRunningId] = useState<string | null>(null);
  /** ⭐ **닫기 하나로 끝나는 실패의 배너는 그 장의 패널과 따로 든다**(`IMAGE_NOT_REQUEUED`.
   *  PRD `7.6.14c` ④). 실패하면 작업을 다시 읽는데, 그 장이 이미 다시 만드는 중이면
   *  `reviveBranchOf` 가 `null` 이 되어 패널째 사라지고 배너도 같이 사라졌다. 셀러가 읽을 틈이
   *  없었다. 토스트가 아니라 배너로 남기는 이유: 문장이 「닫고 확인해 주세요」라 셀러가 닫는
   *  순간을 고르게 해야 한다. 시간이 지나면 사라지는 토스트는 그 순간을 빼앗는다. */
  const [notice, setNotice] = useState<{ text: string; label: string } | null>(null);
  const wanted = picked === undefined ? searchParams.get(IMAGE_PARAM) : picked;
  const fallback =
    picked === undefined ? view.images.find((image) => reviveBranchOf(image, view) !== null) : undefined;
  const pickedImage = wanted ? view.images.find((image) => image.id === wanted) : undefined;

  const revive = useMutation({
    mutationFn: (imageId: string) => reviveImage(view.id, imageId),
    onSuccess: (next) => {
      // 서버가 준 작업을 그대로 캐시에 넣는다. 셸의 머리줄도 같은 키를 본다.
      queryClient.setQueryData(["job", view.id], next);
      void queryClient.invalidateQueries({ queryKey: ["job-events", view.id] });
      void queryClient.invalidateQueries({ queryKey: ["job-billing", view.id] });
      // **답은 그림이다.** 작업이 지금 선 화면으로 돌아간다(`StageIndex`). 대개 `W-31` 이고
      // 방금 비어 있던 칸이 새 결과로 채워져 있다.
      if (follow.current) navigate(`/jobs/${view.id}`);
    },
    onSettled: () => {
      setRunningId(null);
    },
    onError: (error) => {
      const failure = remakeOutcomeMessage(asRemakeError(error), { revive: true });
      if (failure.actions.kind === "DISMISS") {
        setNotice({ text: failure.text, label: failure.actions.label });
      }
      // **실패해도 작업을 다시 읽는다.** 횟수 줄이 같은 사실을 들고 있어야 하고, `답이
      // 없었다` 갈래의 "금액이 들었는지는 위 횟수 줄로 확인하세요"가 이 재조회를 전제로 한다.
      void queryClient.invalidateQueries({ queryKey: ["job", view.id] });
    },
  });

  // **누른 뒤에는 그 장을 붙든다.** 도는 동안 작업이 `PROCESSING` 으로 읽히면 판정이 `null`
  // 이 되는데(`reviveBranchOf` 의 `isJobRunning`), 그때 패널이 사라지면 셀러는 눌렀는지도
  // 모른다.
  const pendingId = revive.isPending ? revive.variables : undefined;
  const target = pendingId
    ? view.images.find((image) => image.id === pendingId)
    : pickedImage && reviveBranchOf(pickedImage, view) !== null
      ? pickedImage
      : fallback;
  const branch = target ? (pendingId ? "REVIVE" : reviveBranchOf(target, view)) : null;

  function choose(imageId: string | null) {
    follow.current = false;
    revive.reset();
    setNotice(null);
    setPicked(imageId);
  }

  return (
    <>
      <PageHead
        frame="W-21"
        title="올리는 중"
        subtitle="장별로 상태가 갈립니다"
        right={`${done} / ${view.images.length}장`}
      />

      <div className="review" style={{ marginTop: 16 }}>
        <div>
          {/* 채우는 순서는 `실패 → 올리는 중 → 대기 → 완료`다(`uploadStageRows`). */}
          <ul className="files">
            {rows.visible.map((image) => {
              const chip = imageStatusLabel(image.status);
              const name = imageName(image);
              const opens = reviveBranchOf(image, view) !== null && runningId === null;
              return (
                <li className="file" key={image.id}>
                  <span className="file-name mono">{image.originalFilename ?? name}</span>
                  <span className="file-dim mono">{imageSubline(image)}</span>
                  <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <StatusChip tone={chip.tone} label={chip.label} />
                    {opens ? (
                      <button
                        type="button"
                        className="iconbtn"
                        aria-label={`${name} 이 장 다시 만들기`}
                        aria-pressed={target?.id === image.id}
                        onClick={() => choose(image.id)}
                      >
                        ↻
                      </button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {foldLine ? (
            <div className="qmore" style={{ marginTop: 9 }}>
              {foldLine}
            </div>
          ) : null}

          {/* 목업 `W-21` 의 이 배너는 **결제 전**의 말이다. 결제가 끝난 작업에서 "다시 열면 이
              화면으로 돌아옵니다"는 거짓이라(작업은 제 단계 화면으로 열린다) 결제 전에만 둔다. */}
          {view.status === "UPLOADED" ? (
            <div style={{ marginTop: 12 }}>
              {/* ⛔ 둘째 줄(「브라우저 탭을 닫아도 올리기는 이어집니다…」, `실행 기준 미정`)을
                  문장째 걷었다 (2026-10-04). 정해지지 않은 동작을 약속하는 문장이라 배지만 떼면
                  확정처럼 읽힌다. 첫 줄은 셀러가 알아야 하는 사실이라 남긴다. */}
              <LimitBanner>결제 전에 그만두면 올린 사진은 지워집니다.</LimitBanner>
            </div>
          ) : null}
        </div>

        <div className="editpanel">
          <section>
            {/* 목업의 `. 열린 상태` 는 상태 표기라 옮기지 않는다. 「돈이 드는 동작은…」 설계
                메모(ScopeBanner)는 걷었다 (2026-10-04). */}
            <h3>다시 만들기</h3>
            {notice ? (
              <div style={{ marginBottom: 9 }}>
                <LimitBanner icon="!">{notice.text}</LimitBanner>
                <div className="drow" style={{ marginTop: 9, display: "flex", gap: 8 }}>
                  <Button label={notice.label} small onClick={() => setNotice(null)} />
                </div>
              </div>
            ) : null}
            {target && branch === "REVIVE" ? (
              <RevivePanel
                view={view}
                image={target}
                pending={revive.isPending}
                // 실패는 **그 장의 것**일 때만 싣는다. 다른 장으로 옮겼는데 지난 장의 실패
                // 배너가 남으면 새 장이 실패한 것으로 읽힌다.
                error={revive.isError && revive.variables === target.id ? revive.error : null}
                // 닫기 하나로 끝나는 실패는 위 `notice` 가 말한다. 같은 문장을 두 번 세우지 않는다.
                noticeShown={notice !== null}
                onRevive={() => {
                  setNotice(null);
                  follow.current = true;
                  setRunningId(target.id);
                  revive.mutate(target.id);
                }}
                onClose={() => choose(null)}
                onOpenStatement={() => navigate(settlementPath(view.id))}
              />
            ) : target && branch === "BLOCKED" ? (
              <BlockedPanel
                view={view}
                image={target}
                onClose={() => choose(null)}
                onOpenStatement={() => navigate(settlementPath(view.id))}
              />
            ) : (
              <div className="drow" style={{ marginTop: 9, display: "flex", gap: 8 }}>
                <Button
                  label="다시 만들기"
                  small
                  disabled
                  disabledReason="다시 만들 장을 목록에서 고르세요. 못 만든 장의 ↻ 를 누르면 여기가 그 장을 가리킵니다."
                />
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ⛔ 발밑 「이 화면에서 아직 못 합니다」(`UploadCantDo`)를 걷었다 (2026-10-04 사용자 지시). */}
    </>
  );
}

/** `{n}번 사진`. **캐러셀 위치가 아니라 올린 순번이다**(PRD `7.6.13b`). 앱 `A-25b` 의 시트
 *  제목과 `W-31` 의 칸 이름이 같은 사진을 같은 이름으로 부른다. */
function imageName(image: JobImageView): string {
  return `${image.sortOrder + 1}번 사진`;
}

/** 앱 `A-25b` 와 같은 갈래. **더 내실 금액이 없다**(2026-09-23 사용자 확정). */
function RevivePanel({
  view,
  image,
  pending,
  error,
  onRevive,
  onClose,
  onOpenStatement,
  noticeShown = false,
}: {
  view: DetailJobView;
  image: JobImageView;
  pending: boolean;
  error: unknown;
  /** 닫기 하나로 끝나는 실패를 바깥(`notice`)이 이미 세웠다. 이 패널은 그 실패를 다시 그리지 않는다. */
  noticeShown?: boolean;
  onRevive: () => void;
  onClose: () => void;
  onOpenStatement: () => void;
}) {
  // 실패 갈래·문구·버튼 모양의 정본은 shared 다(`remake-messages.ts`). 앱 시트와 같다.
  // 이 패널은 못 만든 장 되살리기(`A-25b`)뿐이라 `revive: true` 다. 받기 기간 409 에 돈 문장이
  // 붙지 않는다(금액 줄 `더 내실 금액 · 없음` 이 말한다. PRD `7.6.14c` ③).
  // ⭐ 서버가 받기 기간이 지났다고 판정한 작업이면(#218 `CLOSED`) 누르기 전부터 ③ 모양이다(PRD
  //    `7.6.14c` ⓪ 표). ⛔ `UNKNOWN` 은 열지 않는다.
  //    누른 적이 없으니 `pressed: false` 다.
  const closedOnOpen = isResultReceiveClosed(view)
    ? remakeOutcomeMessage(
        { status: 409, code: RESULT_RECEIVE_EXPIRED_CODE },
        { revive: true, pressed: false },
      )
    : null;
  const rejectedExpired = !!error && isResultReceiveExpired(asRemakeError(error));
  // 지난 실패가 받기 기간 409 가 아니면 `CLOSED` 의 닫힌 모양이 이긴다(앱 시트와 같다).
  const raised = error
    ? closedOnOpen && !rejectedExpired
      ? closedOnOpen
      : remakeOutcomeMessage(asRemakeError(error), { revive: true })
    : closedOnOpen;
  /** 받기 기간 지남이면 본문과 금액 줄을 세우지 않는다(PRD `7.6.14c` ③. 앱 `A-25b` 와 같다). */
  /** 누른 되살리기가 **원본 없는 작업** 409 로 돌아왔다. 버튼이 꺼지는 갈래라 받기 기간과 같은
   *  규칙으로 본문과 금액 줄을 걷는다(앱 시트와 같다). `CLOSED` 와 겹칠 때의 우선순위는 앱 시트
   *  주석과 같이 이번에 바꾸지 않았다. */
  const rejectedDiscarded = !!error && isRemakeRejectedDiscarded(asRemakeError(error));
  const makeClosed = closedOnOpen !== null || rejectedExpired || rejectedDiscarded;
  const failure = raised && noticeShown && raised.actions.kind === "DISMISS" ? null : raised;
  const dismiss = failure?.actions.kind === "DISMISS" ? failure.actions : null;
  const remakeAction = failure?.actions.kind === "REMAKE" ? failure.actions : null;

  return (
    <>
      <p className="hint">
        <span className="l1">{remakeSheetTitle(imageName(image))}</span>
        {makeClosed ? null : (
          <span className="l2">
            {/* 촬영을 가리키는 알려진 원인은 `PROVIDER_REJECTED` 하나다(앱과 같은 조건).
                웹은 `다시 올리는 편` 이다(목업 `W-21`). */}
            {reviveSheetBody("WEB", image.errorCode)}
          </span>
        )}
      </p>
      <div className="setgrp" style={{ marginBottom: 9 }}>
        {/* ⛔ 금액이 없으면 줄을 안 세운다. `₩0` 은 돌아갈 돈이 없다는 거짓말이 된다. */}
        {typeof image.refundableAmount === "number" ? (
          <button type="button" className="setlink clickable" onClick={onOpenStatement}>
            <span>{roundEndAmountLabel(null)}</span>
            <span className="v mono">
              {formatKrw(image.refundableAmount)}{" "}
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </span>
          </button>
        ) : null}
        <span className="setlink">
          <span>{REVIVE_SHEET_COPY.remakeCountLabel}</span>
          <span className="v mono">
            {/* `상한 미정` 배지를 걷었다 (2026-10-04). 횟수는 서버 값이라 남긴다. */}
            {`${jobProviderRecallCount(view)}회`}
          </span>
        </span>
      </div>
      {/* **두 갈래는 낱말이 아니라 금액 줄이 가른다**(목업 `A-25b`). ⛔ `₩0` 을 적지 않는다. */}
      {makeClosed ? null : <PayNow label="더 내실 금액" amount="없음" />}
      <div style={{ marginTop: 9 }}>
        {pending ? (
          <LimitBanner icon="↓">{REVIVE_SHEET_COPY.pendingBanner}</LimitBanner>
        ) : failure ? (
          <LimitBanner icon={failure.icon}>
            {/* 앱 `A-25b` 와 같은 갈래다. 못 만든 장에는 `그대로 있을 결과` 가 없다. */}
            {failure.actions.kind === "REMAKE" && failure.actions.disabled === false
              ? reviveFailedText(image.errorCode)
              : failure.text}
          </LimitBanner>
        ) : (
          <LimitBanner icon="₩">
            <span className="l1">{REVIVE_SHEET_COPY.waitingBanner[0]}</span>
            <span className="l2">{REVIVE_SHEET_COPY.waitingBanner[1]}</span>
          </LimitBanner>
        )}
      </div>
      <div className="drow" style={{ marginTop: 9, display: "flex", gap: 8 }}>
        {dismiss ? (
          <Button label={dismiss.label} small onClick={onClose} />
        ) : (
          <>
            {/* 도는 동안은 `그만두기` 가 아니라 `닫기` 다. 멈출 수 없다. */}
            {/* 받기 기간 409 는 다시 걸 수 없어 `닫기` 다(`closeLabel`. PRD `7.6.14c` ③). */}
            <Button
              label={pending ? "닫기" : (remakeAction?.closeLabel ?? "그만두기")}
              variant="ghost"
              small
              onClick={onClose}
            />
            {pending ? (
              <Button label={REVIVE_SHEET_COPY.pendingLabel} small disabled />
            ) : (
              <Button
                label="다시 만들기"
                small
                disabled={remakeAction?.disabled ?? false}
                {...(remakeAction?.disabledReason
                  ? { disabledReason: remakeAction.disabledReason }
                  : {})}
                onClick={onRevive}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

/** 앱 `A-25c` 와 같은 갈래. **지금은 새 결제를 제안하지 않는다.** ⛔ 금액 줄을 두지 않는다.
 *  ⓘ(막혀 있다)와 ⓘⓘ(보낼 사진이 없다)를 `recallBlockKind` 가 가른다. */
function BlockedPanel({
  view,
  image,
  onClose,
  onOpenStatement,
}: {
  view: DetailJobView;
  image: JobImageView;
  onClose: () => void;
  onOpenStatement: () => void;
}) {
  const reason = recallBlockOf(image, view, view.servedAt);
  const terminal = recallBlockKind(reason) === "TERMINAL";
  const tooLarge = reason === "SOURCE_TOO_LARGE";
  /** 원본이 없는 작업. ⛔ `SOURCE_GONE` 문장으로 그리지 않는다(앱 `A-25c` 와 같은 갈래,
   *  2026-11-01 사용자 결정). 글자는 앱 시트와 같은 한 벌(`REVIVE_BLOCKED_COPY.discarded`)이다. */
  const discarded = reason === "JOB_DISCARDED";
  /** ⭐ **앱 `A-31f` 와 같은 갈래**(PRD `7.6.13g`). ⛔ `SOURCE_GONE` 문장으로 떨어뜨리지 않는다.
   *  문장은 앱 시트와 같은 한 벌(`RETENTION_ENDED_COPY`)이다. */
  const retentionEnded = retentionEndedBlockCopy(reason);

  return (
    <>
      <p className="hint">
        <span className="l1">{reviveBlockedTitle(imageName(image), terminal)}</span>
        <span className="l2">
          {terminal
            ? tooLarge
              ? REVIVE_BLOCKED_COPY.tooLarge.lines.join(" ")
              : discarded
                ? REVIVE_BLOCKED_COPY.discarded.lines.join(" ")
                : retentionEnded
                ? retentionEnded.lines.join(" ")
                : REVIVE_BLOCKED_COPY.sourceGone.lines.join(" ")
            : REVIVE_BLOCKED_COPY.blocked.lines.join(" ")}
        </span>
      </p>
      <div className="setgrp" style={{ marginBottom: 9 }}>
        {typeof image.refundableAmount === "number" ? (
          <button type="button" className="setlink clickable" onClick={onOpenStatement}>
            <span>{roundEndAmountLabel(null)}</span>
            <span className="v mono">
              {formatKrw(image.refundableAmount)}{" "}
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </span>
          </button>
        ) : null}
        {/* ⛔ `다시 되는 때` 줄을 걷었다 (2026-10-04). 값이 `미정` 배지뿐이었다. 풀리면 이 자리에서
            다시 만들 수 있다는 말은 위 설명 줄이 한다. */}
      </div>
      <LimitBanner icon="!">
        {terminal ? REVIVE_BLOCKED_COPY.terminalBanner : REVIVE_BLOCKED_COPY.waitingBanner}
      </LimitBanner>
      <div className="drow" style={{ marginTop: 9, display: "flex", gap: 8 }}>
        <Button label="닫기" variant="ghost" small onClick={onClose} />
        <Button
          label="다시 만들기"
          small
          disabled
          disabledReason={
            terminal
              ? tooLarge
                ? REVIVE_BLOCKED_COPY.tooLarge.disabledReason
                : discarded
                  ? REVIVE_BLOCKED_COPY.discarded.disabledReason
                  : retentionEnded
                  ? retentionEnded.disabledReason
                  : REVIVE_BLOCKED_COPY.sourceGone.disabledReason
              : REVIVE_BLOCKED_COPY.blocked.disabledReason
          }
        />
      </div>
    </>
  );
}

